import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, PaymentDetails } from 'src/Library/Types';
import { Modal, Form, Steps, Button, message, Spin } from 'src/Library/AntdLibComponents';
import { useGenericState } from 'src/Library/UseGenericState';
import { FormComponentProps } from 'antd/lib/form';
import { uploadSIModalStyles } from './UploadSIModal.styles';
import {
  uploadSIStepsMap,
  getSIPartyAddress,
} from 'src/components/pages/shipment-execution/SE.utils';
import UploadSIModalStakeholderAndService from './UploadSIModalStakeholderAndService';
import UploadSIModalContainerAndCargo from './UploadSIModalContainerAndCargo';
import UploadSIModalAdditionalDetails from './UploadSIModalAdditionalDetails';
import {
  fetchShipmentInstructionsDraftDetails,
  saveShippingInstructionsDraft,
  addShipmentInstruction,
  amendShippingInstructions,
  retryShippingInstructions,
  fetchShipmentInstructionsContainers,
  fetchShipmentInstructionsPartiesAddress,
} from 'src/api/shipmentExecution';
import {
  isValidArray,
  isValidString,
  getDateValue,
  isValidNumber,
  clone,
} from 'src/Library/Helper';
import isEmpty from 'lodash/isEmpty';
import Protip from 'src/components/pages/Enquiry/Booking/Protip';
import {
  ShipmentData,
  ShippingInstrutionsData,
  SIPartyAddress,
  StakeHolders,
  ReferenceNumbers,
} from 'src/components/pages/shipment-execution/SE.Types';
import { InquiryType } from 'src/Library/Constants';
import { connect } from 'react-redux';
import { CurrentPage, getMandatoryFields, TabDescription, TabNames } from './UploadSIModal.utils';
import uniqBy from 'lodash/uniqBy';
import { Drawer, Icon } from 'antd';

interface IProps extends StylesProps<ReturnType<typeof uploadSIModalStyles>>, FormComponentProps {
  isVisible: boolean;
  onCancel: () => void;
  onSubmit: (isSuccessful?: boolean) => void;
  shipmentData: ShipmentData;
  taskId: string;
  setDocumentTab: (isDraft?: boolean) => void;
  isDocumentRequestTab?: boolean;
  onSaveDraft: () => void;
  SICountryValidations;
  amendData?: ShippingInstrutionsData;
  isAmend?: boolean;
  isRetryError?: boolean;
}

interface StateProps {
  currentStep: CurrentPage;
  prefillingData;
  isLoading?: boolean;
  isSavingDraft?: boolean;
  isDraftProtipVisible?: boolean;
  isDraftOrAmend?: boolean;
  draftData?: ShippingInstrutionsData;
  addressList?: SIPartyAddress;
  isSubmitting?: boolean;
  countryId?: string; //for field validations
  trackingContainers;
  partyInvolvedWithAddress?;
  vesselVoyageFetchedFromContainer?: {
    vessel?: string;
    voyage?: string;
  };
}

const UploadSIModal = (props: IProps): React.ReactElement => {
  const {
    classes,
    isVisible,
    onCancel,
    form,
    onSubmit,
    shipmentData,
    taskId,
    setDocumentTab,
    isDocumentRequestTab,
    onSaveDraft,
    SICountryValidations,
    amendData,
    isAmend,
    isRetryError,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    currentStep: CurrentPage.FIRST,
    prefillingData: {},
    isDraftOrAmend: isAmend || isRetryError,
    trackingContainers: [],
  });

  const {
    currentStep,
    prefillingData,
    isSavingDraft,
    isDraftProtipVisible,
    isDraftOrAmend,
    draftData,
    isLoading,
    addressList,
    isSubmitting,
    countryId,
    trackingContainers,
    partyInvolvedWithAddress,
    vesselVoyageFetchedFromContainer,
  } = state;

  React.useEffect(() => {
    if (isAmend || isRetryError) {
      setState({ draftData: amendData });
    } else {
      setPrefillingObject();
    }
    fetchContainers();
  }, []);

  React.useEffect(() => {
    prefillDataInForm();
  }, [draftData]);

  React.useEffect(() => {
    form.setFieldsValue({
      ['bookingDetails.vessel']: draftData?.vessel || vesselVoyageFetchedFromContainer?.vessel,
      ['bookingDetails.voyage']: draftData?.voyage || vesselVoyageFetchedFromContainer?.voyage,
    });
  }, [draftData, vesselVoyageFetchedFromContainer]);

  const setAddressList = (addressList) => {
    setState({ addressList });
  };

  const fetchContainers = async () => {
    const response = await fetchShipmentInstructionsContainers({ shipmentId: shipmentData?.id });
    if (response?.isSuccess) {
      setState({
        trackingContainers: response.data?.containerDetails || [],
        vesselVoyageFetchedFromContainer: {
          vessel: response.data?.vessel || undefined,
          voyage: response.data?.voyage || undefined,
        },
      });
    }
  };

  const prefillDataInForm = () => {
    // if amending or draft data is present
    if (isDraftOrAmend) {
      if (isEmpty(draftData)) return;
      const partiesInvolved = draftData?.stakeHolders?.map((item) => ({
        ...item,
        type: item.type,
        address: !isEmpty(item.address)
          ? {
              key: item.address?.id,
              label: getSIPartyAddress(item.address),
            }
          : undefined,
      }));
      const prefillingData = {
        partiesInvolved,
        bookingDetails: {
          bookingNumber: draftData?.bookingNumber,
          carrierId: draftData?.carrier?.id || undefined,
          vessel: draftData?.vessel || vesselVoyageFetchedFromContainer?.vessel || undefined,
          voyage: draftData?.voyage || vesselVoyageFetchedFromContainer?.voyage || undefined,
        },
        locationDetails: {
          movementType: draftData?.movementType || undefined,
          portOfLoadingId: !isEmpty(draftData?.portOfLoading)
            ? {
                key: draftData?.portOfLoading?.id,
                label: `${draftData?.portOfLoading?.name} (${draftData?.portOfLoading?.code})`,
              }
            : undefined,
          portOfDischargeId: !isEmpty(draftData?.portOfDischarge)
            ? {
                key: draftData?.portOfDischarge?.id,
                label: `${draftData?.portOfDischarge?.name} (${draftData?.portOfDischarge?.code})`,
              }
            : undefined,
          placeOfReceiptId: !isEmpty(draftData?.placeOfReceipt)
            ? {
                key: draftData?.placeOfReceipt?.id,
                label: `${draftData?.placeOfReceipt?.name} (${draftData?.placeOfReceipt?.code})`,
              }
            : undefined,
          placeOfDeliveryId: !isEmpty(draftData?.placeOfDelivery)
            ? {
                key: draftData?.placeOfDelivery?.id,
                label: `${draftData?.placeOfDelivery?.name} (${draftData?.placeOfDelivery?.code})`,
              }
            : undefined,
          originAddress: draftData?.originAddress,
          destinationAddress: draftData?.destinationAddress,
          ...(!isEmpty(draftData?.blPrintDetails) && {
            blPrintDetails: {
              pol: draftData?.blPrintDetails?.pol,
              pod: draftData?.blPrintDetails?.pod,
              placeOfReceipt: draftData?.blPrintDetails?.placeOfReceipt,
              placeOfDelivery: draftData?.blPrintDetails?.placeOfDelivery,
            },
          }),
        },
        isDifferentBLDetails: !isEmpty(draftData?.blPrintDetails),
      };
      setState({
        prefillingData: {
          ...prefillingData,
          cargoAndContainerDetails: {
            cargoDetails: {
              ...draftData?.cargoAndContainerDetails?.cargoDetails,
              packageType: !isEmpty(draftData?.cargoAndContainerDetails?.cargoDetails?.packageType)
                ? {
                    key: draftData?.cargoAndContainerDetails?.cargoDetails?.packageType?.id,
                    label:
                      draftData?.cargoAndContainerDetails?.cargoDetails?.packageType?.description,
                  }
                : undefined,
            },
            containerDetails: draftData?.cargoAndContainerDetails?.containerDetails?.map(
              (item) => ({
                ...item,
                sealDetails: isValidArray(item?.sealDetails)
                  ? item?.sealDetails?.map((seal) => ({
                      ...seal,
                      type: seal?.type?.key,
                    }))
                  : [{ type: 'Carrier' }],
                cargoDetails: {
                  ...item.cargoDetails,
                  packageType: !isEmpty(item?.cargoDetails?.packageType)
                    ? {
                        key: item?.cargoDetails?.packageType?.id,
                        label: item?.cargoDetails?.packageType?.description,
                      }
                    : undefined,
                },
              })
            ),
          },
          isOnlyOneCargo: !isEmpty(draftData?.cargoAndContainerDetails?.cargoDetails),
          paymentDetails: draftData?.paymentDetails?.map((item) => ({
            ...item,
            chargeType: item?.chargeType?.key,
            freightTerms: item?.freightTerms?.key,
            payer: item?.payer?.key,
          })) || [{}],
          blInstructions: {
            ...draftData?.blInstructions,
            documentType: draftData?.blInstructions?.documentType?.key,
            blReleaseDate: getDateValue(draftData?.blInstructions?.blReleaseDate?.dateString),
            blReleaseOfficeId: !isEmpty(draftData?.blInstructions?.blReleaseOffice)
              ? {
                  key: draftData?.blInstructions?.blReleaseOffice.id,
                  label: `${draftData?.blInstructions?.blReleaseOffice.name} (${draftData?.blInstructions?.blReleaseOffice.code})`,
                }
              : undefined,
          },
          customsCompliance: {
            ...draftData?.customsCompliance,
            filingStatus: draftData?.customsCompliance?.filingStatus?.key,
            parties: draftData?.customsCompliance?.parties?.map((item) => ({
              ...item,
              address: !isEmpty(item?.address)
                ? {
                    key: item?.address?.id,
                    label: getSIPartyAddress(item.address),
                  }
                : undefined,
            })),
          },
          referenceNumbers: draftData?.referenceNumbers,
          notifyUsers: draftData?.notifyUsers,
          partnerNotifyUsers: draftData?.partnerNotifyUsers,
          isDifferentPartnerNotifications: isValidArray(draftData?.partnerNotifyUsers),
          blComments: draftData?.blComments,
        },
        countryId:
          shipmentData?.type === InquiryType.IMPORT
            ? draftData?.portOfDischarge?.countryId
            : draftData?.portOfLoading?.countryId,
      });
      form.setFieldsValue(
        {
          ...prefillingData,
        },
        () => {
          form.setFieldsValue({
            partiesInvolved,
            ...(!isEmpty(draftData?.blPrintDetails) && {
              locationDetails: {
                blPrintDetails: {
                  pol: draftData?.blPrintDetails?.pol,
                  pod: draftData?.blPrintDetails?.pod,
                  placeOfReceipt: draftData?.blPrintDetails?.placeOfReceipt,
                  placeOfDelivery: draftData?.blPrintDetails?.placeOfDelivery,
                },
              },
            }),
          });
        }
      );
    } else {
      // if draft data is not present
      const prefillingData = {
        ...shipmentData,
        bookingDetails: {
          bookingNumber: shipmentData?.bookingNumber,
          carrierId: shipmentData?.carrierId,
        },
        locationDetails: {
          movementType: shipmentData?.pickupAndDrop,
          portOfLoadingId: shipmentData?.originPolId
            ? {
                key: shipmentData?.originPolId,
                label: `${shipmentData?.originPolName} (${shipmentData?.originPolCode})`,
              }
            : shipmentData?.originPortId
            ? {
                key: shipmentData?.originPortId,
                label: `${shipmentData?.originPortName} (${shipmentData?.originPortCode})`,
              }
            : undefined,
          portOfDischargeId: shipmentData?.destinationPodId
            ? {
                key: shipmentData?.destinationPodId,
                label: `${shipmentData?.destinationPodName} (${shipmentData?.destinationPodCode})`,
              }
            : shipmentData?.destinationPortId
            ? {
                key: shipmentData?.destinationPortId,
                label: `${shipmentData?.destinationPortName} (${shipmentData?.destinationPortCode})`,
              }
            : undefined,
          placeOfReceiptId: shipmentData?.originPortId
            ? {
                key: shipmentData?.originPortId,
                label: `${shipmentData?.originPortName} (${shipmentData?.originPortCode})`,
              }
            : undefined,
          placeOfDeliveryId: shipmentData?.destinationPortId
            ? {
                key: shipmentData?.destinationPortId,
                label: `${shipmentData?.destinationPortName} (${shipmentData?.destinationPortCode})`,
              }
            : undefined,
          originAddress: shipmentData?.originAddress,
          destinationAddress: shipmentData?.destinationAddress,
        },
      };
      setState({
        prefillingData,
        countryId:
          shipmentData?.type === InquiryType.IMPORT
            ? shipmentData?.destinationPodCountryId || shipmentData?.destinationCountryId
            : shipmentData?.originPolCountryId || shipmentData?.originCountryId,
      });
      form.setFieldsValue({
        ...prefillingData,
      });
    }
  };

  const setPrefillingObject = async (): Promise<void> => {
    setState({ isLoading: true });
    const response = await fetchShipmentInstructionsDraftDetails(shipmentData?.id, taskId);

    if (!isValidArray(response?.data?.stakeHolders)) {
      const addressResponse = await fetchShipmentInstructionsPartiesAddress({
        shipmentId: shipmentData?.id,
      });
      if (addressResponse.isSuccess) {
        setAddressList(addressResponse?.data || []);
        const partyInvolvedWithAddress = [
          {
            type: { key: 'Shipper', label: 'Shipper' },
            name: shipmentData?.shipperName,
            address: addressResponse?.data?.['Shipper']?.[0]
              ? {
                  key: addressResponse?.data['Shipper'][0].id,
                  label: getSIPartyAddress(addressResponse?.data['Shipper'][0]),
                }
              : undefined,
          },
          {
            type: { key: 'Consignee', label: 'Consignee' },
            name: shipmentData?.customerName,
            address: addressResponse?.data?.['Consignee']?.[0]
              ? {
                  key: addressResponse?.data['Consignee'][0].id,
                  label: getSIPartyAddress(addressResponse?.data['Consignee'][0]),
                }
              : undefined,
          },
          {
            type: { key: 'FreightForwarder', label: 'Freight Forwarder' },
            name: shipmentData?.ffName,
            address: addressResponse?.data?.['FreightForwarder']?.[0]
              ? {
                  key: addressResponse?.data['FreightForwarder'][0].id,
                  label: getSIPartyAddress(addressResponse?.data['FreightForwarder'][0]),
                }
              : undefined,
          },
          {
            type: { key: 'NotifyParty', label: 'Notify Party' },
          },
        ];
        setState({
          partyInvolvedWithAddress,
        });
        form.setFieldsValue({ partiesInvolved: partyInvolvedWithAddress });
      }
    }
    if (response.isSuccess && !isEmpty(response?.data)) {
      setState({ draftData: response.data, isLoading: false, isDraftOrAmend: true });
    } else {
      setState({ isLoading: false });
    }
  };

  const isNotUniqueEmailList = (userList) => {
    const notifyUsersList = clone(userList);
    const uniqueNotifyUsersList = uniqBy(notifyUsersList, 'email');
    return uniqueNotifyUsersList?.length !== notifyUsersList?.length;
  };

  const handleNext = (): void => {
    form.validateFieldsAndScroll(async (err, values) => {
      if (err) {
        return;
      }
      setState({
        prefillingData: {
          ...prefillingData,
          ...values,
        },
      });

      if (currentStep === CurrentPage.FIRST) {
        handleStepChange(CurrentPage.SECOND);
      } else if (currentStep === CurrentPage.SECOND) {
        handleStepChange(CurrentPage.THIRD);
      } else {
        const formValues = form.getFieldsValue();
        const body = createAPIObject({
          ...prefillingData,
          ...formValues,
        });
        const mandatoryFields = getMandatoryFields({
          SICountryValidations,
          countryId,
          shipmentData,
        });
        const { mandatoryReferenceNumbers, isFilingStatusMandatory } = mandatoryFields;

        if (isFilingStatusMandatory && !isValidString(body?.customsCompliance?.filingStatus)) {
          message.error('Filing Status is required');
          return;
        }

        if (isValidArray(mandatoryReferenceNumbers)) {
          const selectedReferencenumbers = body?.referenceNumbers?.map((item) => item.type);
          const missingReferencenumbers = mandatoryReferenceNumbers.filter(
            (item) => !selectedReferencenumbers?.includes(item)
          );

          if (isValidArray(missingReferencenumbers)) {
            message.error(
              `Reference number(s) ${missingReferencenumbers.join(', ')}
               ${missingReferencenumbers.length > 1 ? 'are' : 'is'} required`,
              10
            );
            return;
          }
        }

        if (isValidArray(body?.notifyUsers)) {
          if (isNotUniqueEmailList(body?.notifyUsers)) {
            message.error('Email Ids of Users for Carrier notifications must be unique');
            return;
          }
        }

        if (isValidArray(body?.partnerNotifyUsers)) {
          if (isNotUniqueEmailList(body?.partnerNotifyUsers)) {
            message.error('Email Ids of Users for INTTRA notifications must be unique');
            return;
          }
        }

        setState({ isSubmitting: true });
        const response = await getApi(body);
        if (response.isSuccess) {
          onSubmit(true);
        } else {
          message.error(response.errorMessage);
        }
        setState({ isSubmitting: false });
      }
    });
  };

  const getApi = (body) => {
    if (isAmend) {
      return amendShippingInstructions(shipmentData?.id, draftData?.draftId, body);
    }
    if (isRetryError) {
      return retryShippingInstructions(shipmentData?.id, draftData?.draftId, body);
    }
    return addShipmentInstruction(shipmentData?.id, taskId, draftData?.draftId, body);
  };

  const handlePrevious = (): void => {
    const currentFormValues = form.getFieldsValue();
    setState({
      prefillingData: {
        ...prefillingData,
        ...currentFormValues,
      },
    });
    if (currentStep === CurrentPage.THIRD) {
      handleStepChange(CurrentPage.SECOND);
    }
    if (currentStep === CurrentPage.SECOND) {
      handleStepChange(CurrentPage.FIRST);
    }
  };

  const handleStepChange = (newStep: CurrentPage): void => {
    setState({ currentStep: newStep });
  };

  const createAPIObject = (formValues, isDraft?) => {
    if (isEmpty(formValues)) return;
    let partiesInvolved: StakeHolders[] = [];
    formValues.partiesInvolved?.forEach((item) => {
      if (isValidString(item.type?.key) && (isDraft || isValidString(item.name))) {
        partiesInvolved.push({
          ...item,
          type: item.type?.key,
          address: item.address?.key,
        });
      }
    });
    let paymentDetails: PaymentDetails[] = [];
    formValues.paymentDetails?.forEach((item) => {
      if (isDraft || isValidString(item.name)) {
        paymentDetails.push({
          ...item,
        });
      }
    });
    let referenceNumbers: ReferenceNumbers[] = [];
    formValues.referenceNumbers?.forEach((item) => {
      if (isValidString(item.value) && isValidString(item.type?.key)) {
        referenceNumbers.push({
          ...item,
          type: item.type?.key,
        });
      }
    });
    const body = {
      partiesInvolved,
      referenceNumbers,
      paymentDetails,
      bookingDetails: formValues.bookingDetails,
      locationDetails: {
        ...formValues.locationDetails,
        portOfLoadingId: formValues.locationDetails?.portOfLoadingId?.key,
        portOfDischargeId: formValues.locationDetails?.portOfDischargeId?.key,
        placeOfReceiptId: formValues.locationDetails?.placeOfReceiptId?.key,
        placeOfDeliveryId: formValues.locationDetails?.placeOfDeliveryId?.key,
      },
      ...(formValues.isOnlyOneCargo && {
        cargoDetails: {
          ...formValues.cargoAndContainerDetails?.cargoDetails,
          packageType: formValues.cargoAndContainerDetails?.cargoDetails?.packageType?.key,
          grossWeight: isValidNumber(
            formValues.cargoAndContainerDetails?.cargoDetails?.grossWeight?.value
          )
            ? formValues.cargoAndContainerDetails?.cargoDetails?.grossWeight
            : undefined,
          grossVolume: isValidNumber(
            formValues.cargoAndContainerDetails?.cargoDetails?.grossVolume?.value
          )
            ? formValues.cargoAndContainerDetails?.cargoDetails?.grossVolume
            : undefined,
        },
      }),
      containerDetails: formValues.cargoAndContainerDetails?.containerDetails?.map((item) => ({
        ...item,
        containerTareWeight: isValidNumber(item.containerTareWeight?.value)
          ? item.containerTareWeight
          : undefined,
        containerGrossWeight: isValidNumber(item?.containerGrossWeight?.value)
          ? item?.containerGrossWeight
          : undefined,
        sealDetails: item.sealDetails?.filter((seal) => isValidString(seal.value)),
        cargoDetails: {
          ...item?.cargoDetails,
          grossWeight: isValidNumber(item?.cargoDetails?.grossWeight?.value)
            ? item?.cargoDetails?.grossWeight
            : undefined,
          packageType: item?.cargoDetails?.packageType?.key,
          grossVolume: isValidNumber(item?.cargoDetails?.grossVolume?.value)
            ? item?.cargoDetails?.grossVolume
            : undefined,
        },
      })),
      ...(!isEmpty(formValues.blInstructions) && {
        blInstructions: {
          ...formValues.blInstructions,
          blReleaseDate: {
            dateString: getDateValue(formValues.blInstructions?.blReleaseDate)?.format(
              'YYYY-MM-DD'
            ),
            time: getDateValue(formValues.blInstructions?.blReleaseDate)?.format('23:59'),
          },
          blReleaseOfficeId: formValues.blInstructions?.blReleaseOfficeId?.key,
        },
      }),
      ...(!isEmpty(formValues.customsCompliance) && {
        customsCompliance: {
          ...formValues.customsCompliance,
          parties: formValues.customsCompliance?.parties?.map((item, index) => ({
            type: index === 0 ? 'Shipper' : 'Consignee',
            name: item?.name,
            address: item?.address?.key,
          })),
        },
      }),
      notifyUsers: formValues.notifyUsers?.filter((item) => isValidString(item.name)),
      partnerNotifyUsers: formValues.partnerNotifyUsers?.filter((item) => isValidString(item.name)),
      blComments: formValues?.blComments,
    };
    return body;
  };

  const renderProtip = (): React.ReactElement => {
    return (
      <Protip
        visible={isDraftProtipVisible}
        success={'Great News!'}
        message1={
          <span>
            Form has been successfully saved to draft.{' '}
            <a onClick={() => setDocumentTab()}>Click here</a> to view saved draft.
          </span>
        }
        message2={'Now you are being redirected to Tasks view'}
        okay={'Okay'}
        onOkay={() => {
          if (isDocumentRequestTab) {
            onSaveDraft();
          }
          onSubmit();
        }}
      />
    );
  };

  const handleSaveDraft = async (): Promise<void> => {
    setState({ isSavingDraft: true });
    const formValues = form.getFieldsValue();
    const body = createAPIObject(
      {
        ...prefillingData,
        ...formValues,
      },
      true
    );

    const response = await saveShippingInstructionsDraft(
      shipmentData?.id,
      taskId,
      draftData?.draftId,
      body
    );
    if (response.isSuccess) {
      setState({ isDraftProtipVisible: true, isSavingDraft: false });
    } else {
      message.error(response.errorMessage);
      setState({ isSavingDraft: false });
    }
  };

  const renderPreviousButton = () => {
    if (currentStep === CurrentPage.FIRST) {
      return;
    }
    return (
      <Button onClick={handlePrevious} type="link">
        <Icon type="left"/>
        Previous
      </Button>
    );
  }

  const renderNextButton = () => {
    if (currentStep === CurrentPage.THIRD) {
      return;
    }
    return (
      <Button onClick={handleNext} type="link">
          Next
        <Icon type="right"/>
      </Button>
    );
  }

  const renderFooter = (): React.ReactElement => {
    return (
      <div className={classes.footer}>
        <div className={classes.justifySpaceBetween}>
          {renderPreviousButton()}
          {renderNextButton()}
        </div>
      </div>
    );
  };

  const setCountryId = (countryId): void => {
    setState({ countryId });
  };

  const renderSaveDraftButton = () => {
    if (isAmend || isRetryError) {
      return;
    }

    return (
      <Button
        className={classes.saveDraftButton}
        type="ghost"
        disabled={isLoading || isSubmitting}
        onClick={() => handleSaveDraft()}
        loading={isSavingDraft}
      >
        Save Draft
      </Button>
    );
  }

  const renderSubmitButton = () => { 
    if (!(currentStep === CurrentPage.THIRD)) {
      return;
    }

    return (
      <Button
        type="primary"
        onClick={handleNext}
        disabled={isLoading || isSavingDraft}
        className={classes.submitButton}
        loading={isSubmitting}
      >
        Submit
      </Button>
    )
  }

  const renderHeader = (title: string) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div className = {classes.header}>
          <Icon
            type="close"
            onClick={onCancel}
          />
          <span
            style={{ marginLeft: 10 }}>
            {title}
          </span>
        </div>
        <div>
          {renderSaveDraftButton()}
          {renderSubmitButton()}
        </div>
      </div>
    );
  }

  const renderFilledCircle = (page: CurrentPage) => {
    const activePage = page === currentStep;
    if (activePage) {
      return <div
        className={classes.filledCircle}
      />;
    }
    return null;
  };

  const renderCircle = (page: CurrentPage) => {
    const activePage = page === currentStep;
    return <div
      className={classes.tabIcon}
      style={{
        border: activePage ? '2px solid #006EC3' : '1px solid #333333',
        backgroundColor: activePage ? '#006EC3' : '#FFF',
      }}
    >
      {renderFilledCircle(page)}
    </div>;
  };

  const complete = () => {
    return <Icon
      type="check-circle"
      theme="filled"
      className={classes.greenCheck}
    />;
  };

  const renderTabIcon = (page: CurrentPage) => {
    if (currentStep === page) {
      return renderCircle(page);
    }
    switch (page) {
      case CurrentPage.FIRST: {
        return complete();
      }
      case CurrentPage.SECOND: {
        if (currentStep === CurrentPage.FIRST) {
          return renderCircle(page);
        }
        return complete();
      }
      case CurrentPage.THIRD: {
        return renderCircle(page);
      }
    }
  };

  const renderTabName = (page: CurrentPage) => {
    return <div
      className={classes.tabName}
    >
      {TabNames[page]}
    </div>;
  };

  const renderTabDescription = (page: CurrentPage) => {
    return <div
      className={classes.tabDescription}
    >
      {TabDescription[page]}
    </div>;
  };

  const renderTabDetails = (page: CurrentPage) => {
    const activePage = page === currentStep;
    return <div
      style={{
        color: activePage ? '#111111' : '#666666',
        fontWeight: activePage ?  'bold' : 'normal',
      }}
    >
      {renderTabName(page)}
      {renderTabDescription(page)}
    </div>;
  };

  const handleTabChange = (page: CurrentPage) => {
    switch (page) {
      case CurrentPage.FIRST: {
        if (currentStep !== CurrentPage.FIRST) {
          handlePrevious();
        }
        return;
      }
      case CurrentPage.SECOND: {
        if (currentStep === CurrentPage.FIRST) {
          handleNext();
        }
        if (currentStep === CurrentPage.THIRD) {
          handlePrevious();
        }
        return;
      }
      case CurrentPage.THIRD: {
        if (currentStep !== CurrentPage.THIRD) {
          handleNext();
        }
        return;
      }
    }
  }

  const renderTab = (page: CurrentPage) => {
    const activePage = page === currentStep;
    return <div
      className={classes.tabSelector}
      onClick={() => handleTabChange(page)}
      style={{ borderBottom: activePage ? '2px solid #006EC3' : 'none' }}
    >
      {renderTabIcon(page)}
      {renderTabDetails(page)}
    </div>;
  };

  const renderPageSelector = () => {
    return <div
      className={classes.pageSelector}>
      {Object.keys(CurrentPage).map((page: CurrentPage) => renderTab(page))}
    </div>;
  };

  return (
    <Drawer
      visible={isVisible}
      width={"85%"}
      title={renderHeader('Upload SI')}
      onClose={onCancel}
      className={classes.main}
      closable={false}
    >
      {isLoading && (
        <div
          style={{
            height: '100%',
            width: '100%',
            textAlign: 'center',
            top: '30%',
            zIndex: 1200,
            position: 'absolute',
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {renderPageSelector()}
      {currentStep === CurrentPage.FIRST && (
        <UploadSIModalStakeholderAndService
          isLoading={isLoading}
          form={form}
          prefillingData={prefillingData}
          shipmentData={shipmentData}
          setAddressList={setAddressList}
          addressList={addressList}
          setCountryId={setCountryId}
          countryId={countryId}
          draftData={draftData}
          partyInvolvedWithAddress={partyInvolvedWithAddress}
        />
      )}
      {currentStep === CurrentPage.SECOND && (
        <UploadSIModalContainerAndCargo
          form={form}
          shipmentData={shipmentData}
          prefillingData={prefillingData}
          countryId={countryId}
          trackingContainers={trackingContainers}
        />
      )}
      {currentStep === CurrentPage.THIRD && (
        <UploadSIModalAdditionalDetails
          form={form}
          prefillingData={prefillingData}
          shipmentData={shipmentData}
          setAddressList={setAddressList}
          addressList={addressList}
          countryId={countryId}
        />
      )}
      {isDraftProtipVisible && renderProtip()}
      {renderFooter()}
    </Drawer>
  );
};

const mapStateToProps = ({ masterData }, ownProps) => ({
  SICountryValidations: masterData.shippingInstructionsMasterData?.SICountryValidations,
});

export default connect(
  mapStateToProps,
  null
)(React.memo(withStyles(uploadSIModalStyles)(Form.create()(UploadSIModal))));
